import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import { Sparkles, FileUp, Loader2, Users, BookUser } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

const INITIAL_FORM = {
  property_address: "", property_type: "single_family", transaction_side: "buyer", status: "active",
  contract_date: "", closing_date: "", earnest_money_deadline: "", inspection_deadline: "",
  financing_deadline: "", appraisal_deadline: "", insurance_deadline: "", possession_date: "",
  purchase_price: "", earnest_money: "",
  buyer_name: "", buyer_email: "", buyer_phone: "",
  seller_name: "", seller_email: "", seller_phone: "",
  buyer_agent_name: "", buyer_agent_email: "", buyer_agent_phone: "",
  seller_agent_name: "", seller_agent_email: "", seller_agent_phone: "",
  buyer2_name: "", buyer2_email: "", buyer2_phone: "",
  seller2_name: "", seller2_email: "", seller2_phone: "",
  buyer_agent_assistant_name: "", buyer_agent_assistant_email: "", buyer_agent_assistant_phone: "",
  seller_agent_assistant_name: "", seller_agent_assistant_email: "", seller_agent_assistant_phone: "",
  lender_name: "", lender_email: "", lender_phone: "", lender_company: "",
  title_company_name: "", title_company_contact_name: "", title_company_email: "", title_company_phone: "",
  home_warranty_company: "", home_warranty_paid_by: "", home_warranty_cost: "",
  workflow_template_id: undefined, notes: "",
};

const PartyBlock = ({ label, prefix, formData, setFormData, fields, contacts = [] }) => {
  const [focusedField, setFocusedField] = useState(null);
  const [query, setQuery] = useState("");
  const wrapperRef = useRef(null);

  // Identify name, email, phone keys from the fields array
  const nameKey = fields.find(f => f.lbl === "Name" || f.lbl === "Contact Name")?.key;
  const emailKey = fields.find(f => f.key.includes("email"))?.key;
  const phoneKey = fields.find(f => f.key.includes("phone"))?.key;

  // Filter contacts by what the user is typing in the name field
  const suggestions = nameKey && focusedField === nameKey && query.length >= 1
    ? contacts.filter(c => c.name && c.name.toLowerCase().includes(query.toLowerCase())).slice(0, 5)
    : [];

  const pickContact = (contact) => {
    const updates = {};
    if (nameKey) updates[nameKey] = contact.name || "";
    if (emailKey && contact.email) updates[emailKey] = contact.email;
    if (phoneKey && contact.phone) updates[phoneKey] = contact.phone;
    setFormData({ ...formData, ...updates });
    setFocusedField(null);
    setQuery("");
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setFocusedField(null);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  return (
    <div className="p-4 bg-[var(--t-bg)] rounded-md border border-[var(--t-border)]" ref={wrapperRef}>
      <Label className="text-sm font-semibold text-[var(--t-text)] mb-3 block">{label}</Label>
      <div className={`grid grid-cols-${fields.length > 3 ? '2' : '3'} gap-3${fields.length > 3 ? ' mb-3' : ''}`}>
        {fields.slice(0, fields.length > 3 ? 2 : 3).map(({ key, lbl, placeholder, type }) => (
          <div className="form-group relative" key={key}>
            <Label className="text-xs text-[var(--t-text-muted)]">
              {lbl}
              {key === nameKey && contacts.length > 0 && (
                <BookUser className="w-3 h-3 inline ml-1 text-[var(--t-text-subtle)]" />
              )}
            </Label>
            <Input
              type={type || "text"}
              value={formData[key]}
              onChange={(e) => {
                setFormData({ ...formData, [key]: e.target.value });
                if (key === nameKey) { setQuery(e.target.value); setFocusedField(key); }
              }}
              onFocus={() => { if (key === nameKey) { setFocusedField(key); setQuery(formData[key] || ""); } }}
              placeholder={placeholder}
              autoComplete="off"
              data-testid={`input-${key.replace(/_/g, '-')}`}
            />
            {key === nameKey && suggestions.length > 0 && (
              <div className="absolute z-50 left-0 right-0 top-full mt-1 rounded-md border border-[var(--t-border)] bg-[var(--t-card)] shadow-lg overflow-hidden" data-testid={`autocomplete-${prefix}`}>
                {suggestions.map((c) => (
                  <button
                    key={c.contact_id}
                    type="button"
                    className="w-full text-left px-3 py-2 text-xs hover:bg-[var(--t-primary)]/10 transition-colors flex items-center justify-between"
                    onMouseDown={(e) => { e.preventDefault(); pickContact(c); }}
                    data-testid={`pick-${prefix}-${c.contact_id}`}
                  >
                    <div className="min-w-0">
                      <p className="font-medium text-[var(--t-text)] truncate">{c.name}</p>
                      <p className="text-[10px] text-[var(--t-text-muted)] truncate">
                        {c.email || "No email"}{c.phone ? ` · ${c.phone}` : ""}
                      </p>
                    </div>
                    <span className="text-[10px] text-[var(--t-text-subtle)] capitalize ml-2 flex-shrink-0">{c.contact_type?.replace(/_/g, " ") || ""}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
      {fields.length > 3 && (
        <div className={`grid grid-cols-${Math.min(fields.length - 2, 3)} gap-3`}>
          {fields.slice(2).map(({ key, lbl, placeholder, type }) => (
            <div className="form-group relative" key={key}>
              <Label className="text-xs text-[var(--t-text-muted)]">
                {lbl}
                {key === nameKey && contacts.length > 0 && (
                  <BookUser className="w-3 h-3 inline ml-1 text-[var(--t-text-subtle)]" />
                )}
              </Label>
              <Input
                type={type || "text"}
                value={formData[key]}
                onChange={(e) => {
                  setFormData({ ...formData, [key]: e.target.value });
                  if (key === nameKey) { setQuery(e.target.value); setFocusedField(key); }
                }}
                onFocus={() => { if (key === nameKey) { setFocusedField(key); setQuery(formData[key] || ""); } }}
                placeholder={placeholder}
                autoComplete="off"
                data-testid={`input-${key.replace(/_/g, '-')}`}
              />
              {key === nameKey && suggestions.length > 0 && (
                <div className="absolute z-50 left-0 right-0 top-full mt-1 rounded-md border border-[var(--t-border)] bg-[var(--t-card)] shadow-lg overflow-hidden" data-testid={`autocomplete-${prefix}`}>
                  {suggestions.map((c) => (
                    <button
                      key={c.contact_id}
                      type="button"
                      className="w-full text-left px-3 py-2 text-xs hover:bg-[var(--t-primary)]/10 transition-colors flex items-center justify-between"
                      onMouseDown={(e) => { e.preventDefault(); pickContact(c); }}
                      data-testid={`pick-${prefix}-${c.contact_id}`}
                    >
                      <div className="min-w-0">
                        <p className="font-medium text-[var(--t-text)] truncate">{c.name}</p>
                        <p className="text-[10px] text-[var(--t-text-muted)] truncate">
                          {c.email || "No email"}{c.phone ? ` · ${c.phone}` : ""}
                        </p>
                      </div>
                      <span className="text-[10px] text-[var(--t-text-subtle)] capitalize ml-2 flex-shrink-0">{c.contact_type?.replace(/_/g, " ") || ""}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const CreateTransactionModal = ({ open, onOpenChange, workflows, contacts = [], onCreated }) => {
  const [formData, setFormData] = useState({ ...INITIAL_FORM });
  const [analyzingContract, setAnalyzingContract] = useState(false);
  const [pendingDocument, setPendingDocument] = useState(null);
  const fileInputRef = useRef(null);

  const resetForm = () => {
    setFormData({ ...INITIAL_FORM });
    setPendingDocument(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        status: formData.status || "active",
        purchase_price: formData.purchase_price ? parseFloat(formData.purchase_price) : null,
        earnest_money: formData.earnest_money ? parseFloat(formData.earnest_money) : null,
        home_warranty_cost: formData.home_warranty_cost ? parseFloat(formData.home_warranty_cost) : null,
        home_warranty_paid_by: formData.home_warranty_paid_by || null,
        home_warranty_company: formData.home_warranty_company || null,
        contract_date: formData.contract_date + "T12:00:00",
        closing_date: formData.closing_date + "T12:00:00",
        earnest_money_deadline: formData.earnest_money_deadline ? formData.earnest_money_deadline + "T12:00:00" : null,
        inspection_deadline: formData.inspection_deadline ? formData.inspection_deadline + "T12:00:00" : null,
        financing_deadline: formData.financing_deadline ? formData.financing_deadline + "T12:00:00" : null,
        appraisal_deadline: formData.appraisal_deadline ? formData.appraisal_deadline + "T12:00:00" : null,
        insurance_deadline: formData.insurance_deadline ? formData.insurance_deadline + "T12:00:00" : null,
        possession_date: formData.possession_date ? formData.possession_date + "T12:00:00" : null,
      };

      const txnResponse = await axios.post(`${API}/transactions`, payload, { withCredentials: true });

      if (pendingDocument && txnResponse.data?.transaction_id) {
        try {
          await axios.post(`${API}/transactions/${txnResponse.data.transaction_id}/attach-contract`, pendingDocument, { withCredentials: true });
        } catch (err) { console.error("Contract attachment failed:", err); }
      }

      toast.success("Transaction created successfully");
      onOpenChange(false);
      resetForm();
      onCreated();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create transaction");
    }
  };

  const handleContractUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setAnalyzingContract(true);
    toast.info("AI is analyzing the contract...", { duration: 15000 });
    try {
      const formDataUpload = new FormData();
      formDataUpload.append("file", file);
      const response = await axios.post(`${API}/transactions/analyze-contract`, formDataUpload, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (response.data.extracted_fields) {
        const data = response.data.extracted_fields;
        if (response.data.pending_document) setPendingDocument(response.data.pending_document);
        setFormData(prev => ({
          ...prev,
          property_address: data.property_address || prev.property_address,
          purchase_price: data.purchase_price || prev.purchase_price,
          earnest_money: data.earnest_money || prev.earnest_money,
          earnest_money_deadline: data.earnest_money_deadline || prev.earnest_money_deadline,
          contract_date: data.contract_date || prev.contract_date,
          closing_date: data.closing_date || prev.closing_date,
          inspection_deadline: data.inspection_deadline || prev.inspection_deadline,
          financing_deadline: data.financing_deadline || prev.financing_deadline,
          appraisal_deadline: data.appraisal_deadline || prev.appraisal_deadline,
          insurance_deadline: data.insurance_deadline || prev.insurance_deadline,
          possession_date: data.possession_date || prev.possession_date,
          property_type: data.property_type || prev.property_type,
          buyer_name: data.buyer1_name || prev.buyer_name,
          buyer2_name: data.buyer2_name || prev.buyer2_name,
          seller_name: data.seller1_name || prev.seller_name,
          seller2_name: data.seller2_name || prev.seller2_name,
          buyer_agent_name: data.buyer_agent_name || prev.buyer_agent_name,
          buyer_agent_email: data.buyer_agent_email || prev.buyer_agent_email,
          buyer_agent_phone: data.buyer_agent_phone || prev.buyer_agent_phone,
          seller_agent_name: data.seller_agent_name || prev.seller_agent_name,
          seller_agent_email: data.seller_agent_email || prev.seller_agent_email,
          seller_agent_phone: data.seller_agent_phone || prev.seller_agent_phone,
          transaction_side: data.transaction_side || prev.transaction_side,
          lender_name: data.lender_name || prev.lender_name,
          title_company_name: data.title_company_name || prev.title_company_name,
        }));
        toast.success("Contract analyzed! Review the extracted fields below.");
      } else {
        toast.warning("Could not fully parse the contract. Some fields may need manual entry.");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to analyze contract");
    } finally {
      setAnalyzingContract(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="create-transaction-modal">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold [font-family:var(--t-heading-font)]">New Transaction</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Contract Upload */}
          <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-[var(--t-text)]/10 rounded-md">
                <Sparkles className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-[var(--t-text)]">AI Contract Reader</h3>
                <p className="text-xs text-[var(--t-text-muted)]">Upload a contract to auto-fill fields</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <input ref={fileInputRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" onChange={handleContractUpload} className="hidden" id="contract-upload" data-testid="contract-upload-input" />
              <Button type="button" variant="outline" className="flex-1 border-blue-300 hover:bg-blue-50" onClick={() => fileInputRef.current?.click()} disabled={analyzingContract} data-testid="upload-contract-btn">
                {analyzingContract ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Analyzing Contract...</>) : (<><FileUp className="w-4 h-4 mr-2" />Upload Contract (PDF or Image)</>)}
              </Button>
            </div>
          </div>

          {/* Property Details */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-[var(--t-text)] uppercase tracking-wider">Property Details</h3>
            <div className="form-group">
              <Label htmlFor="property_address">Property Address *</Label>
              <Input id="property_address" value={formData.property_address} onChange={(e) => setFormData({ ...formData, property_address: e.target.value })} required data-testid="input-property-address" />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="form-group">
                <Label>Status</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                  <SelectTrigger data-testid="select-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active_listing">Active Listing</SelectItem>
                    <SelectItem value="active">Before Inspections</SelectItem>
                    <SelectItem value="through_inspections">Through Inspections</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="form-group">
                <Label>Property Type</Label>
                <Select value={formData.property_type} onValueChange={(v) => setFormData({ ...formData, property_type: v })}>
                  <SelectTrigger data-testid="select-property-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="single_family">Single Family</SelectItem>
                    <SelectItem value="condo">Condo</SelectItem>
                    <SelectItem value="townhouse">Townhouse</SelectItem>
                    <SelectItem value="multi_family">Multi-Family</SelectItem>
                    <SelectItem value="land">Land</SelectItem>
                    <SelectItem value="commercial">Commercial</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="form-group">
                <Label>Our Representation is:</Label>
                <Select value={formData.transaction_side} onValueChange={(v) => setFormData({ ...formData, transaction_side: v })}>
                  <SelectTrigger data-testid="select-transaction-side"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="buyer">Buyer Side</SelectItem>
                    <SelectItem value="seller">Seller Side</SelectItem>
                    <SelectItem value="dual">Dual Agency</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Key Dates */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-[var(--t-text)] uppercase tracking-wider">Key Dates</h3>
            <div className="grid grid-cols-2 gap-4">
              {[
                { key: "contract_date", label: "Contract Date *", req: true },
                { key: "closing_date", label: "Closing Date *", req: true },
                { key: "earnest_money_deadline", label: "Earnest Money Deadline" },
                { key: "inspection_deadline", label: "Inspection Deadline" },
                { key: "financing_deadline", label: "Financing Deadline" },
                { key: "appraisal_deadline", label: "Appraisal Deadline" },
                { key: "insurance_deadline", label: "Insurance Deadline" },
                { key: "possession_date", label: "Possession Date" },
              ].map(({ key, label, req }) => (
                <div className="form-group" key={key}>
                  <Label htmlFor={key}>{label}</Label>
                  <Input id={key} type="date" value={formData[key]} onChange={(e) => setFormData({ ...formData, [key]: e.target.value })} required={req} data-testid={`input-${key.replace(/_/g, '-')}`} />
                </div>
              ))}
            </div>
          </div>

          {/* Financial */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-[var(--t-text)] uppercase tracking-wider">Financial</h3>
            <div className="grid grid-cols-2 gap-4">
              {[
                { key: "purchase_price", label: "Purchase Price" },
                { key: "earnest_money", label: "Earnest Money" },
              ].map(({ key, label }) => (
                <div className="form-group" key={key}>
                  <Label htmlFor={key}>{label}</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--t-text-muted)]">$</span>
                    <Input
                      id={key}
                      placeholder="0.00"
                      className="pl-7"
                      value={formData[key] ? Number(formData[key]).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : ""}
                      onChange={(e) => { const raw = e.target.value.replace(/[^0-9.]/g, ''); setFormData({ ...formData, [key]: raw }); }}
                      data-testid={`input-${key.replace(/_/g, '-')}`}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Transaction Parties */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-[var(--t-text)] uppercase tracking-wider">
              <Users className="w-4 h-4 inline mr-2" />Transaction Parties
            </h3>
            <PartyBlock label="Buyer 1" prefix="buyer" formData={formData} setFormData={setFormData} contacts={contacts} fields={[
              { key: "buyer_name", lbl: "Name", placeholder: "Buyer name" },
              { key: "buyer_email", lbl: "Email", placeholder: "buyer@email.com", type: "email" },
              { key: "buyer_phone", lbl: "Phone", placeholder: "(555) 555-5555" },
            ]} />
            <PartyBlock label="Buyer 2" prefix="buyer2" formData={formData} setFormData={setFormData} contacts={contacts} fields={[
              { key: "buyer2_name", lbl: "Name", placeholder: "Buyer 2 name" },
              { key: "buyer2_email", lbl: "Email", placeholder: "buyer2@email.com", type: "email" },
              { key: "buyer2_phone", lbl: "Phone", placeholder: "(555) 555-5555" },
            ]} />
            <PartyBlock label="Buyer's Agent" prefix="buyer_agent" formData={formData} setFormData={setFormData} contacts={contacts} fields={[
              { key: "buyer_agent_name", lbl: "Name", placeholder: "Agent name" },
              { key: "buyer_agent_email", lbl: "Email", placeholder: "agent@email.com", type: "email" },
              { key: "buyer_agent_phone", lbl: "Phone", placeholder: "(555) 555-5555" },
            ]} />
            <PartyBlock label="Buyer's Agent Assistant" prefix="buyer_agent_assistant" formData={formData} setFormData={setFormData} contacts={contacts} fields={[
              { key: "buyer_agent_assistant_name", lbl: "Name", placeholder: "Assistant name" },
              { key: "buyer_agent_assistant_email", lbl: "Email", placeholder: "assistant@email.com", type: "email" },
              { key: "buyer_agent_assistant_phone", lbl: "Phone", placeholder: "(555) 555-5555" },
            ]} />
            <PartyBlock label="Seller 1" prefix="seller" formData={formData} setFormData={setFormData} contacts={contacts} fields={[
              { key: "seller_name", lbl: "Name", placeholder: "Seller name" },
              { key: "seller_email", lbl: "Email", placeholder: "seller@email.com", type: "email" },
              { key: "seller_phone", lbl: "Phone", placeholder: "(555) 555-5555" },
            ]} />
            <PartyBlock label="Seller 2" prefix="seller2" formData={formData} setFormData={setFormData} contacts={contacts} fields={[
              { key: "seller2_name", lbl: "Name", placeholder: "Seller 2 name" },
              { key: "seller2_email", lbl: "Email", placeholder: "seller2@email.com", type: "email" },
              { key: "seller2_phone", lbl: "Phone", placeholder: "(555) 555-5555" },
            ]} />
            <PartyBlock label="Seller's Agent" prefix="seller_agent" formData={formData} setFormData={setFormData} contacts={contacts} fields={[
              { key: "seller_agent_name", lbl: "Name", placeholder: "Agent name" },
              { key: "seller_agent_email", lbl: "Email", placeholder: "agent@email.com", type: "email" },
              { key: "seller_agent_phone", lbl: "Phone", placeholder: "(555) 555-5555" },
            ]} />
            <PartyBlock label="Seller's Agent Assistant" prefix="seller_agent_assistant" formData={formData} setFormData={setFormData} contacts={contacts} fields={[
              { key: "seller_agent_assistant_name", lbl: "Name", placeholder: "Assistant name" },
              { key: "seller_agent_assistant_email", lbl: "Email", placeholder: "assistant@email.com", type: "email" },
              { key: "seller_agent_assistant_phone", lbl: "Phone", placeholder: "(555) 555-5555" },
            ]} />
            <PartyBlock label="Lender" prefix="lender" formData={formData} setFormData={setFormData} contacts={contacts} fields={[
              { key: "lender_company", lbl: "Company Name", placeholder: "Lender company" },
              { key: "lender_name", lbl: "Contact Name", placeholder: "Loan officer name" },
              { key: "lender_email", lbl: "Email", placeholder: "lender@email.com", type: "email" },
              { key: "lender_phone", lbl: "Phone", placeholder: "(555) 555-5555" },
            ]} />
            <PartyBlock label="Title Company" prefix="title_company" formData={formData} setFormData={setFormData} contacts={contacts} fields={[
              { key: "title_company_name", lbl: "Company Name", placeholder: "Title company" },
              { key: "title_company_contact_name", lbl: "Contact Name", placeholder: "Contact person" },
              { key: "title_company_email", lbl: "Email", placeholder: "title@email.com", type: "email" },
              { key: "title_company_phone", lbl: "Phone", placeholder: "(555) 555-5555" },
            ]} />
          </div>

          {/* Home Warranty */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-[var(--t-text)] uppercase tracking-wider">Home Warranty</h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="form-group">
                <Label htmlFor="home_warranty_company">Home Warranty Company</Label>
                <Input id="home_warranty_company" value={formData.home_warranty_company} onChange={(e) => setFormData({ ...formData, home_warranty_company: e.target.value })} placeholder="Company name" data-testid="input-home-warranty-company" />
              </div>
              <div className="form-group">
                <Label htmlFor="home_warranty_paid_by">Paid for by</Label>
                <Select value={formData.home_warranty_paid_by || "none"} onValueChange={(v) => setFormData({ ...formData, home_warranty_paid_by: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="select-home-warranty-paid-by"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">--</SelectItem>
                    <SelectItem value="buyer">Buyer</SelectItem>
                    <SelectItem value="seller">Seller</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="form-group">
                <Label htmlFor="home_warranty_cost">Cost</Label>
                <Input id="home_warranty_cost" type="number" step="0.01" value={formData.home_warranty_cost} onChange={(e) => setFormData({ ...formData, home_warranty_cost: e.target.value })} placeholder="$0.00" data-testid="input-home-warranty-cost" />
              </div>
            </div>
          </div>

          {/* Workflow Template */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-[var(--t-text)] uppercase tracking-wider">Workflow</h3>
            <div className="form-group">
              <Label>Apply Workflow Template</Label>
              <Select value={formData.workflow_template_id || "none"} onValueChange={(v) => setFormData({ ...formData, workflow_template_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="select-workflow"><SelectValue placeholder="Select a workflow template..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No template</SelectItem>
                  {workflows.map(wf => (<SelectItem key={wf.template_id} value={wf.template_id}>{wf.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Notes */}
          <div className="form-group">
            <Label htmlFor="notes">Notes</Label>
            <Textarea id="notes" placeholder="Add any additional notes..." value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={3} data-testid="input-notes" />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" className="btn-primary" data-testid="submit-transaction">Create Transaction</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
